import { BookFormat } from '@masters-club/types';
import type { LanguageCode, TranslationLanguage } from '@/components/translations';
import { FormField, FormCard } from '@/components/forms';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import { FileUploadField, type FileRecord } from './FileUploadField';
import { PricingTierSelect } from '@/components/shared/pricing-tier-select';
import { BookTranslationsTab } from './BookTranslationsTab';
import {
  BOOK_FORMAT_OPTIONS,
  type Book,
  type BookFormData,
  type BookTranslationData,
} from '../types';

export interface BookBasicState {
  slug: string;
  format: BookFormat;
  cover: FileRecord | null;
  sourceFile: FileRecord | null;
  author: string;
  languageCode: LanguageCode;
  translationGroupId: string | null;
  pricingTierId: number | null;
  totalPages: number | null;
  totalDurationSec: number | null;
  isPublished: boolean;
  /**
   * Название для авто-генерации slug (не отправляется, используется только в UI).
   */
  ruTitle: string;
}

type SetAction = {
  [K in keyof BookBasicState]: { type: 'SET'; field: K; value: BookBasicState[K] };
}[keyof BookBasicState];

type Action =
  | SetAction
  | { type: 'RESET'; state: BookBasicState };

export function bookBasicReducer(s: BookBasicState, a: Action): BookBasicState {
  switch (a.type) {
    case 'SET':
      return { ...s, [a.field]: a.value };
    case 'RESET':
      return a.state;
    default:
      return s;
  }
}

const CYR: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo',
  ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
  ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function isBookFormat(value: string): value is BookFormat {
  return (
    value === BookFormat.EPUB
    || value === BookFormat.PDF
    || value === BookFormat.FB2
    || value === BookFormat.AUDIO
  );
}

export function toBookSlug(text: string): string {
  return text.toLowerCase()
    .replaceAll(/[а-яё]/g, (c) => CYR[c] ?? c)
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .slice(0, 80);
}

export function initBookState(book?: Book): BookBasicState {
  if (!book) {
    return {
      slug: '',
      format: BookFormat.EPUB,
      cover: null,
      sourceFile: null,
      author: '',
      languageCode: 'ru',
      translationGroupId: null,
      pricingTierId: null,
      totalPages: null,
      totalDurationSec: null,
      isPublished: false,
      ruTitle: '',
    };
  }

  return {
    slug: book.slug,
    format: book.format,
    cover: book.coverFileId && book.coverFileUrl
      ? { id: book.coverFileId, url: book.coverFileUrl }
      : null,
    sourceFile: book.sourceFileId && book.sourceFileUrl
      ? { id: book.sourceFileId, url: book.sourceFileUrl, name: book.sourceFileOriginalName ?? undefined }
      : null,
    author: book.author ?? '',
    languageCode: book.languageCode,
    translationGroupId: book.translationGroupId,
    pricingTierId: book.pricingTierId,
    totalPages: book.totalPages,
    totalDurationSec: book.totalDurationSec,
    isPublished: book.isPublished,
    ruTitle: book.translations.find((t) => t.languageCode === 'ru')?.title ?? '',
  };
}

export function buildBookFormData(
  s: BookBasicState,
  translations: BookFormData['translations'],
): BookFormData {
  const isAudio = s.format === BookFormat.AUDIO;
  if (!isAudio && s.sourceFile === null) {
    throw new Error('sourceFileId is required');
  }
  return {
    slug: s.slug,
    format: s.format,
    coverFileId: s.cover?.id ?? null,
    sourceFileId: isAudio ? null : (s.sourceFile?.id ?? null),
    author: s.author.trim() || null,
    languageCode: s.languageCode,
    translationGroupId: s.translationGroupId,
    pricingTierId: s.pricingTierId,
    totalPages: isAudio ? null : s.totalPages,
    totalDurationSec: isAudio ? null : null,
    translations,
  };
}

interface BookTranslationsProps {
  translations: BookTranslationData[];
  onTranslationsChange: (next: BookTranslationData[]) => void;
  languages: TranslationLanguage[];
  activeLanguage: string;
  onActiveLanguageChange: (code: string) => void;
}

interface BookBasicFormProps {
  state: BookBasicState;
  dispatch: React.Dispatch<Action>;
  isEdit: boolean;
  errors: Record<string, string>;
  translations?: BookTranslationsProps;
}

/**
 * Базовые поля книги: slug, format, обложка, sourceFile, язык, автор, публикация.
 * Переводы и главы редактируются отдельными табами.
 */
export function BookBasicForm({
  state,
  dispatch,
  isEdit,
  errors,
  translations,
}: BookBasicFormProps) {
  function set<K extends keyof BookBasicState>(field: K, value: BookBasicState[K]): void {
    const action = { type: 'SET' as const, field, value };
    dispatch(action as SetAction);
  }

  const isAudio = state.format === BookFormat.AUDIO;

  const handleSlugBlur = () => {
    if (!state.slug && state.ruTitle) {
      set('slug', toBookSlug(state.ruTitle));
    }
  };

  const handleTitleChange = (title: string) => {
    set('ruTitle', title);
    if (!isEdit && !state.slug) {
      // auto-generate slug пока он пустой
      set('slug', toBookSlug(title));
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <FormCard title="Основное">
          <FormField label="Название (RU)" name="ruTitle" required hint="Используется для авто-генерации slug и в списке книг.">
            <Input
              value={state.ruTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Например: Мастер и Маргарита"
              className={errors.ruTitle ? 'border-destructive' : ''}
            />
            {errors.ruTitle && <p className="text-sm text-destructive mt-1">{errors.ruTitle}</p>}
          </FormField>

          <FormField label="Slug (URL)" name="slug" required hint="Только латиница, цифры, дефисы.">
            <Input
              value={state.slug}
              onChange={(e) => {
                const sanitized = e.target.value
                  .toLowerCase()
                  .replaceAll(/[а-яё]/g, (c) => CYR[c] ?? c)
                  .replaceAll(/[^a-z0-9-]/g, '-')
                  .replaceAll(/-{2,}/g, '-');
                set('slug', sanitized);
              }}
              onBlur={handleSlugBlur}
              placeholder="master-i-margarita"
              className={errors.slug ? 'border-destructive' : ''}
            />
            {errors.slug && <p className="text-sm text-destructive mt-1">{errors.slug}</p>}
          </FormField>

          <FormField label="Автор (fallback)" name="author" hint="Используется, если нет перевода BookTranslation.authorDisplay.">
            <Input
              value={state.author}
              onChange={(e) => set('author', e.target.value)}
              placeholder="Михаил Булгаков"
            />
          </FormField>
        </FormCard>

        <FormCard title="Обложка">
          <FileImageUpload
            value={state.cover}
            onChange={(file) => set('cover', file)}
            aspectRatio="2/3"
          />
        </FormCard>

        {!isAudio && (
          <FormCard
            title="Исходный файл книги"
            description="EPUB / PDF / FB2. Обязательное поле."
          >
            <FileUploadField
              value={state.sourceFile}
              onChange={(file) => set('sourceFile', file)}
              accept=".epub,.pdf,.fb2,.zip"
              placeholder="Загрузить EPUB/PDF/FB2"
              maxSizeMB={500}
            />
            {errors.sourceFile && <p className="text-sm text-destructive mt-1">{errors.sourceFile}</p>}
          </FormCard>
        )}

        {!isAudio && (
          <FormCard title="Объём">
            <FormField label="Всего страниц" name="totalPages" hint="Заполняется при обработке автоматически, но можно переопределить.">
              <Input
                type="number"
                min={1}
                value={state.totalPages ?? ''}
                onChange={(e) => set('totalPages', e.target.value ? parseInt(e.target.value, 10) : null)}
                placeholder="320"
              />
            </FormField>
          </FormCard>
        )}

        {translations && (
          <FormCard
            title="Переводы"
            description="Название, автор и описание для каждого языка книги."
          >
            <BookTranslationsTab
              value={translations.translations}
              onChange={translations.onTranslationsChange}
              languages={translations.languages}
              activeLanguage={translations.activeLanguage}
              onLanguageChange={translations.onActiveLanguageChange}
            />
          </FormCard>
        )}
      </div>

      <div className="space-y-6">
        <FormCard title="Настройки">
          <FormField label="Формат" name="format">
            <Select
              value={state.format}
              onValueChange={(value) => {
                if (isBookFormat(value)) {
                  set('format', value);
                }
              }}
              disabled={isEdit}
            >
              <SelectTrigger>
                <SelectValue>
                  {BOOK_FORMAT_OPTIONS.find((o) => o.value === state.format)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {BOOK_FORMAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const desc = BOOK_FORMAT_OPTIONS.find((o) => o.value === state.format)?.description;
              return desc ? (
                <p className="text-xs text-muted-foreground mt-1">{desc}</p>
              ) : null;
            })()}
            {isEdit && (
              <p className="text-xs text-muted-foreground mt-1">
                Формат нельзя менять после создания книги.
              </p>
            )}
          </FormField>

          <FormField label="Основной язык" name="languageCode" hint="ISO 639-1 код (2 символа), например ru, en, uz.">
            <Input
              value={state.languageCode}
              onChange={(e) => set('languageCode', e.target.value.toLowerCase().slice(0, 2))}
              placeholder="ru"
              maxLength={2}
            />
          </FormField>

          <FormField
            label="Тариф"
            name="pricingTierId"
            hint="Выберите существующий PricingTier для платной книги. Управление тарифами — в разделе «Тарифы»."
          >
            <PricingTierSelect
              value={state.pricingTierId}
              onChange={(id) => set('pricingTierId', id)}
              placeholder="Бесплатная книга"
            />
          </FormField>

          <div className="flex items-center justify-between pt-2">
            <Label htmlFor="book-published">Опубликована</Label>
            <Switch
              id="book-published"
              checked={state.isPublished}
              onCheckedChange={(v) => set('isPublished', v)}
            />
          </div>
        </FormCard>
      </div>
    </div>
  );
}
